export const STATUSES = ["New", "Contacted", "Scheduled", "Closed"];

export const STATUS_LABELS = {
  New: "New Inquiry",
  Contacted: "Contacted",
  Scheduled: "Session Scheduled",
  Closed: "Closed: Signed Up for Tier 1",
};

export const stageLabel = (s) => STATUS_LABELS[s] || s;

export const TEMPERATURES = ["Hot", "Warm", "Cold"];

export const TEMP_STYLE = {
  Hot: "bg-rose-50 text-rose-700 border-rose-200",
  Warm: "bg-amber-50 text-amber-700 border-amber-200",
  Cold: "bg-sky-50 text-sky-700 border-sky-200",
};

export const TEMP_DOT = {
  Hot: "bg-rose-500",
  Warm: "bg-amber-500",
  Cold: "bg-sky-500",
};

export const STATUS_STYLE = {
  New: "bg-[#F6F2FB] text-[#3B185F] border-[#3B185F]/20",
  Contacted: "bg-sky-50 text-sky-800 border-sky-200",
  Scheduled: "bg-[#FFFDF0] text-[#8a6a10] border-[#D4AF37]/40",
  Enrolled: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Closed: "bg-stone-100 text-stone-700 border-stone-300",
  Draft: "bg-stone-100 text-stone-700 border-stone-200",
  Sent: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Expired: "bg-red-50 text-red-700 border-red-200",
  Deactivated: "bg-stone-100 text-stone-600 border-stone-200",
  "Not Yet Active": "bg-amber-50 text-amber-800 border-amber-200",
  Open: "bg-[#F6F2FB] text-[#3B185F] border-[#3B185F]/20",
  "In Progress": "bg-sky-50 text-sky-800 border-sky-200",
  Submitted: "bg-[#FFFDF0] text-[#8a6a10] border-[#D4AF37]/40",
  Completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Needs Correction": "bg-red-50 text-red-700 border-red-200",
};

export const REQUEST_LABEL = { Open: "New", "In Progress": "In Progress", Submitted: "Awaiting review", Completed: "Completed", "Needs Correction": "Needs Correction" };

export const CRM_FIELDS = [
  { key: "first_name", label: "First Name", required: true, match: ["first name", "firstname", "first", "given name"] },
  { key: "last_name", label: "Last Name", match: ["last name", "lastname", "last", "surname"] },
  { key: "address", label: "Property Address", match: ["property address", "address", "street address", "mailing address"] },
  { key: "email", label: "Email", match: ["email", "e-mail", "email address"] },
  { key: "phone", label: "Phone 1", match: ["phone 1", "phone1", "phone", "mobile", "phone number", "cell"] },
  { key: "phone2", label: "Phone 2", match: ["phone 2", "phone2", "secondary phone", "alt phone", "home phone", "work phone"] },
];

export const fullName = (c) => `${c.first_name} ${c.last_name || ""}`.trim();

export const fmtDateTime = (s) =>
  new Date(s).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

export const fmtTime = (s) => new Date(s).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export const roleLabel = (r) => (r === "admin" ? "Owner" : "AVY Associate");

export function parseCsv(text) {
  const rows = [];
  let row = [], cell = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') q = false;
      else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === ",") { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell); cell = "";
      if (row.some((x) => x.trim() !== "")) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((x) => x.trim() !== "")) rows.push(row);
  const [headers = [], ...data] = rows;
  return { headers: headers.map((h) => h.trim()), rows: data };
}

const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export function downloadCsv(contacts, filename = "avy-contacts-export.csv") {
  const head = ["First Name", "Last Name", "Property Address", "Email", "Phone 1", "Phone 2", "Temperature", "Labels", "Notes", "Status"];
  const lines = contacts.map((c) =>
    [c.first_name, c.last_name, c.address, c.email, c.phone, c.phone2, c.temperature, (c.tags || []).join("; "), c.notes, c.status].map(esc).join(",")
  );
  const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export const titleCase = (s) => (s || "").trim().replace(/\S+/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

export const TOKEN = "{{First Name}}";

export const TEMPLATES = [
  { id: "blank", name: "Blank email", subject: "", body: "" },
  {
    id: "welcome",
    name: "Welcome to AVY",
    subject: "Welcome to AVY, {{First Name}}!",
    body: "Hi {{First Name}},\n\nThank you for your interest in AVY. We help busy professionals reclaim their time with trained Virtual Assistants.\n\nReply to this email any time with questions.\n\nWarmly,\nThe AVY Team",
  },
  {
    id: "discovery",
    name: "Discovery Call Invite",
    subject: "{{First Name}}, let's book your AVY discovery call",
    body: "Hi {{First Name}},\n\nWe'd love to learn about your business on a free 20-minute discovery call.\n\nPick a time that works for you and we'll handle the rest.\n\nBest,\nThe AVY Team",
  },
  {
    id: "checkin",
    name: "Friendly Check-in",
    subject: "Quick check-in, {{First Name}}",
    body: "Hi {{First Name}},\n\nJust checking in to see how things are going. Is there anything AVY can help you with this month?\n\nTalk soon,\nThe AVY Team",
  },
];

export const DRIP_PRESET = {
  name: "AVY Development Program",
  steps: [
    { day: 0, subject: "Welcome to the AVY Development Program, {{First Name}}!", body: "Hi {{First Name}},\n\nWelcome to the AVY Development Program! Over the next few weeks we'll guide you from your first inquiry all the way to Tier 1 certification.\n\nWarmly,\nThe AVY Team" },
    { day: 2, subject: "{{First Name}}, here's your next step", body: "Hi {{First Name}},\n\nThanks for your interest! Here's what to expect next and how to get the most from the program.\n\nTalk soon,\nThe AVY Team" },
    { day: 5, subject: "Your AVY Development Program overview", body: "Hi {{First Name}},\n\nHere's a full overview: the skills you'll build, the live sessions you'll attend, and the Tier 1 certification at the end.\n\nBest,\nThe AVY Team" },
    { day: 9, subject: "Your training session is confirmed, {{First Name}}", body: "Hi {{First Name}},\n\nYour upcoming training session is confirmed. Please review the prep materials before we meet.\n\nSee you soon,\nThe AVY Team" },
    { day: 16, subject: "How's your progress, {{First Name}}?", body: "Hi {{First Name}},\n\nJust checking in on your progress through the program. Reply any time with questions — we're here to help.\n\nCheers,\nThe AVY Team" },
    { day: 30, subject: "Congratulations on your AVY certification, {{First Name}}!", body: "Hi {{First Name}},\n\nCongratulations — you've completed the AVY Development Program and earned your Tier 1 certification! We're so proud of you.\n\nWith gratitude,\nThe AVY Team" },
  ],
};

export const personalize = (text, contact) =>
  (text || "").replaceAll("{{First Name}}", contact?.first_name || "there").replaceAll("{First Name}", contact?.first_name || "there");
